import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalSaidaService } from '@shared/services/apis/orquestrador-vendas/nota-fiscal-saida.service';
import { ExcluirFinanceiroNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/excluir-financeiro-nota-fiscal-saida.request';
import { ContasReceberNFeSaidaLancadaStruct } from '@shared/services/structs/financas/contas-receber-nfe-saida-lancada.struct';
import { MovimentacaoNFeSaidaLancadaStruct } from '@shared/services/structs/financas/movimentacao-nfe-saida-lancada.struct';

import { NfeFinanceiroLancamentosComponent } from '../nfe-financeiro-lancamentos/nfe-financeiro-lancamentos.component';

export interface NfeExcluirFinanceiroDialogData {
    idNFeSaida: number;
    /** "NF-e 123 / 1": o rótulo do cabeçalho. */
    rotuloDocumento: string;
}

/**
 * Excluir financeiro da NF-e de saída (T19; cópia de `pv-excluir-financeiro-dialog`, com a justificativa obrigatória
 * de 10 a 500 caracteres da NF-e de entrada). Lista o que o Finanças tem da nota com a ação prevista por item ou o
 * motivo que impede: qualquer bloqueio recusa a exclusão inteira, então o botão fica desabilitado. Fecha com `true`
 * quando excluiu.
 */
@Component({
    selector: 'app-nfe-excluir-financeiro-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        AlertCardComponent,
        LoadingBoxComponent,
        NfeFinanceiroLancamentosComponent,
    ],
    templateUrl: './nfe-excluir-financeiro-dialog.component.html',
    styleUrls: ['./nfe-excluir-financeiro-dialog.component.css'],
})
export class NfeExcluirFinanceiroDialogComponent implements OnInit {
    public isLoading = true;
    public isExcluindo = false;
    public contas: ContasReceberNFeSaidaLancadaStruct[] = [];
    public movimentacoes: MovimentacaoNFeSaidaLancadaStruct[] = [];

    /** Mesma regra do servidor: obrigatória, de 10 a 500 caracteres (sem os espaços das pontas). */
    public readonly justificativa = new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(10), Validators.maxLength(500)],
    });

    constructor(
        public dialogRef: MatDialogRef<NfeExcluirFinanceiroDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: NfeExcluirFinanceiroDialogData,
        private notaFiscalSaidaService: NotaFiscalSaidaService,
        private alertService: AlertService,
    ) {}

    /** Sem o que o Finanças tem da nota não há o que mostrar nem confirmar: avisa por toast e fecha. */
    public ngOnInit(): void {
        this.notaFiscalSaidaService.getFinanceiro(this.data.idNFeSaida).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível consultar o financeiro da nota.', AlertType.warning);
                    this.dialogRef.close(false);
                    return;
                }

                this.contas = res.contas ?? [];
                this.movimentacoes = res.movimentacoes ?? [];
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Aviso', 'Não foi possível consultar o financeiro da nota agora. Tente novamente em alguns instantes.', AlertType.warning);
                this.dialogRef.close(false);
            },
        });
    }

    public get temFinanceiro(): boolean {
        return this.contas.length > 0 || this.movimentacoes.length > 0;
    }

    /** Qualquer item bloqueado recusa a exclusão inteira (tudo ou nada). */
    public get bloqueios(): string[] {
        const parcelas = this.contas.flatMap((c) => c.parcelas).map((p) => p.motivoBloqueioExclusao);
        const movimentacoes = this.movimentacoes.map((m) => m.motivoBloqueioExclusao);

        return [...parcelas, ...movimentacoes].filter((m): m is string => !!m);
    }

    /** Sem nada no Finanças, confirmar só atualiza o registro da nota (o orquestrador registra a exclusão que faltou). */
    public get podeExcluir(): boolean {
        return !this.isLoading && this.bloqueios.length === 0;
    }

    public get tooltipExcluir(): string {
        if (this.isLoading || this.podeExcluir) return '';
        return 'A exclusão está bloqueada: resolva os itens indicados no Finanças e tente de novo.';
    }

    public confirmar(): void {
        if (!this.podeExcluir || this.isExcluindo) return;

        this.justificativa.setValue(this.justificativa.value.trim());
        this.justificativa.markAsTouched();
        if (this.justificativa.invalid) {
            this.alertService.show('Aviso', 'Informe a justificativa com ao menos 10 caracteres para excluir o financeiro.', AlertType.warning);
            return;
        }

        this.isExcluindo = true;
        const request = new ExcluirFinanceiroNotaFiscalSaidaRequest();
        request.justificativa = this.justificativa.value;

        this.notaFiscalSaidaService.excluirFinanceiro(this.data.idNFeSaida, request).subscribe({
            next: (res) => {
                this.isExcluindo = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível excluir o financeiro da nota.', AlertType.warning);
                    return;
                }

                // Excluiu: o aviso (cobrança não cancelada, registro na nota pendente) vai no toast de aviso.
                if (res.avisoDescricao) {
                    this.alertService.show('Aviso', res.avisoDescricao, AlertType.warning);
                } else {
                    this.alertService.show('Financeiro excluído', 'O financeiro da nota foi excluído do Finanças.', AlertType.success);
                }
                this.dialogRef.close(true);
            },
            error: () => {
                this.isExcluindo = false;
                this.alertService.show('Aviso', 'Não foi possível excluir o financeiro agora. Tente novamente em alguns instantes.', AlertType.warning);
            },
        });
    }

    public fechar(): void {
        this.dialogRef.close(false);
    }
}
